import { Box } from "@mui/material";

/**
 * A status, rendered as a filled block rather than an outline.
 *
 * Outlined chips of different colours read as the same shape until you get to the word, which is
 * exactly wrong for the pairs that matter most — Approved vs Rejected, Paid vs Overdue. A shaded
 * ground carries the state before the label is read.
 *
 * Keyed on a semantic TONE, not on a status: invoices, adjustments and anything after them already
 * decide what a status means, and only borrow how it looks.
 */
export type StatusTone = "neutral" | "info" | "warning" | "orange" | "success" | "error" | "muted";

/** Light ground, dark ink — legible at 12px and calm enough to sit in a dense table. */
const TONE: Record<StatusTone, { bg: string; fg: string }> = {
  neutral: { bg: "#f0efed", fg: "#5a564f" },
  info: { bg: "#e8eef8", fg: "#1e4a7b" },
  warning: { bg: "#fdf3e0", fg: "#8a5a00" },
  // Sits between warning and error on purpose: a reversible hold is not a failure, but it is not
  // a scheduled-and-fine state either. Ink/ground clears AA at 5.51:1.
  orange: { bg: "#fdeee0", fg: "#9a4a07" },
  success: { bg: "#e7f4ea", fg: "#1e6b34" },
  error: { bg: "#fdeceb", fg: "#b3261e" },
  // For a state that is over and carries no signal — it should recede, not compete.
  muted: { bg: "transparent", fg: "var(--color-graphite)" },
};

export default function StatusBadge({
  label,
  tone = "neutral",
  size = "sm",
}: {
  label: string;
  tone?: StatusTone;
  size?: "sm" | "md";
}) {
  const { bg, fg } = TONE[tone];
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        px: size === "md" ? 1.5 : 1.1,
        py: size === "md" ? 0.5 : 0.35,
        borderRadius: "4px",
        bgcolor: bg,
        color: fg,
        opacity: tone === "muted" ? 0.7 : 1,
        fontSize: size === "md" ? 12 : 11,
        fontWeight: 700,
        letterSpacing: "0.4px",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Box>
  );
}
